import { useNavigate } from "react-router-dom";
import {
    FaMicrochip,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaEnvelope,
    FaExternalLinkAlt,
    FaArrowUp,
    FaChevronRight,
    FaPaperPlane,
    FaUserCheck,
    FaGlobe,
} from "react-icons/fa";

import containerStyles from "../pages/Container.module.css";
import styles from "./Footer.module.css";

import ieelogo from "../../assets/images/ieee-logo.png";
import edslogo from "../../assets/images/ieee-eds-logo.png";
import iveclogo from "../../assets/images/ivecicon.png";
import mtrdclogo from "../../assets/images/mtrdc-logo.jpeg";

const CONFERENCE_LINKS = [
    { label: "About IVEC 2027", path: "/" },
    { label: "Conference Topics", path: "/topics" },
    { label: "Technical Programme", path: "/programme" },
    { label: "Calendar of Events", path: "/calendar-section" },
    { label: "Destination & Venue", path: "/venue-section" },
];

const AUTHOR_LINKS = [
    { label: "Author Instructions", path: "/author-instructions" },
    {
        label: "Submit Paper (EDAS)",
        url: "https://edas.info/newPaper.php?c=35646",
        external: true,
    },
    { label: "IVEC Committee", path: "/ivec" },
    { label: "EDC Technical Committee", path: "/edc" },
    { label: "Awards & Honors", path: "/awards" },
];

const DELEGATE_LINKS = [
    { label: "Delegate Registration", path: "/registration" },
    { label: "VISA Assistance & Letters", path: "/visa-help" },
    { label: "Participant Info Form", path: "/participant-info" },
    { label: "Sponsors & Exhibitors", path: "/sponsors" },
    { label: "Delegate Portal / Login", path: "/login" },
];

const Footer = () => {
    const navigate = useNavigate();

    const handleNavigate = (path) => {
        if (!path) return;
        navigate(path);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <footer className={styles.footer}>
            {/* Top accent line */}
            <div className={styles.topAccentLine}></div>

            <div className={`container ${containerStyles.cscontainer}`}>
                {/* -------------------------------------------------------------
                    1. PARTNERS & TECHNICAL CO-SPONSORSHIP SHOWCASE
                ------------------------------------------------------------- */}
                <div className={styles.partnerRibbon}>
                    <div className={styles.partnerGroup}>
                        <span className={styles.partnerLabel}>JOINTLY ORGANIZED BY</span>
                        <div className={styles.partnerItems}>
                            <div
                                className={styles.partnerCard}
                                title="Vacuum Electronic Devices and Applications Society"
                            >
                                <span className={styles.vedasBadge}>VEDAS</span>
                                <div className={styles.partnerMeta}>
                                    <strong>VEDAS India</strong>
                                    <small>Bengaluru Chapter</small>
                                </div>
                            </div>

                            <div className={styles.partnerDivider}></div>

                            <div
                                className={styles.partnerCard}
                                title="Microwave Tube Research & Development Centre (MTRDC), DRDO"
                            >
                                <div className={styles.partnerLogoWrap}>
                                    <img
                                        src={mtrdclogo}
                                        alt="MTRDC DRDO Logo"
                                        className={styles.partnerLogo}
                                    />
                                </div>
                                <div className={styles.partnerMeta}>
                                    <strong>MTRDC (DRDO)</strong>
                                    <small>Ministry of Defence, Govt. of India</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.ribbonDivider}></div>

                    <div className={styles.partnerGroup}>
                        <span className={styles.partnerLabel}>TECHNICAL CO-SPONSORSHIP</span>
                        <div className={styles.partnerItems}>
                            <div
                                className={styles.partnerCard}
                                title="IEEE Electron Devices Society"
                            >
                                <div className={styles.partnerLogoWrap}>
                                    <img
                                        src={edslogo}
                                        alt="IEEE EDS Logo"
                                        className={styles.partnerLogo}
                                    />
                                </div>
                                <div className={styles.partnerMeta}>
                                    <strong>IEEE EDS</strong>
                                    <small>Electron Devices Society</small>
                                </div>
                            </div>

                            <div className={styles.partnerDivider}></div>

                            <div
                                className={styles.partnerCard}
                                title="Institute of Electrical and Electronics Engineers"
                            >
                                <div className={styles.partnerLogoWrap}>
                                    <img
                                        src={ieelogo}
                                        alt="IEEE Logo"
                                        className={styles.partnerLogo}
                                    />
                                </div>
                                <div className={styles.partnerMeta}>
                                    <strong>IEEE</strong>
                                    <small>Advancing Tech for Humanity</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* -------------------------------------------------------------
                    2. MAIN FOOTER CONTENT GRID
                ------------------------------------------------------------- */}
                <div className="row g-4 g-lg-5">
                    {/* Brand & Overview Column */}
                    <div className="col-12 col-lg-4">
                        <div
                            className={styles.footerBrand}
                            onClick={() => handleNavigate("/")}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && handleNavigate("/")}
                        >
                            <div className={styles.footerBrandIcon}>
                                <img
                                    src={iveclogo}
                                    alt="IVEC Icon"
                                    className={styles.ivecBrandImg}
                                />
                            </div>
                            <div>
                                <strong className={styles.brandTitle}>IVEC 2027</strong>
                                <span className={styles.brandSubtitle}>
                                    28th International Vacuum Electronics Conference
                                </span>
                            </div>
                        </div>

                        <p className={styles.brandDesc}>
                            The premier global platform bringing together international
                            scientists, engineers, researchers, and industry pioneers to
                            advance vacuum electronics, RF/microwave sources, accelerators,
                            and high-power vacuum devices.
                        </p>

                        {/* Snapshot Box: Date & Venue */}
                        <div className={styles.eventSnapshot}>
                            <div className={styles.snapshotItem}>
                                <div className={styles.snapshotIconBox}>
                                    <FaCalendarAlt />
                                </div>
                                <div>
                                    <span className={styles.snapshotLabel}>Conference Dates</span>
                                    <strong className={styles.snapshotValue}>
                                        March 15 – 18, 2027
                                    </strong>
                                </div>
                            </div>
                            <div className={styles.snapshotItem}>
                                <div className={styles.snapshotIconBox}>
                                    <FaMapMarkerAlt />
                                </div>
                                <div>
                                    <span className={styles.snapshotLabel}>Venue & City</span>
                                    <strong className={styles.snapshotValue}>
                                        Hilton Garden Inn Bengaluru Embassy Manyata Business Park, Bengaluru, India
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {/* Quick CTA Buttons */}
                        <div className={styles.brandActions}>
                            <button
                                type="button"
                                className={styles.primaryCta}
                                onClick={() => handleNavigate("/registration")}
                            >
                                <FaUserCheck /> Register Now
                            </button>
                            <a
                                href="https://edas.info/newPaper.php?c=35646"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.secondaryCta}
                            >
                                <FaPaperPlane /> Submit Paper{" "}
                                <FaExternalLinkAlt className={styles.miniExt} />
                            </a>
                        </div>
                    </div>

                    {/* Column 2: Conference */}
                    <div className="col-6 col-md-4 col-lg-2">
                        <h4 className={styles.columnTitle}>Conference</h4>
                        <div className={styles.linkList}>
                            {CONFERENCE_LINKS.map((item, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={styles.footerLink}
                                    onClick={() => handleNavigate(item.path)}
                                >
                                    <FaChevronRight className={styles.linkArrow} />
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Column 3: Authors & Papers */}
                    <div className="col-6 col-md-4 col-lg-2">
                        <h4 className={styles.columnTitle}>Authors & Papers</h4>
                        <div className={styles.linkList}>
                            {AUTHOR_LINKS.map((item, idx) =>
                                item.external ? (
                                    <a
                                        key={idx}
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.footerLink}
                                    >
                                        <FaChevronRight className={styles.linkArrow} />
                                        <span>{item.label}</span>
                                        <FaExternalLinkAlt className={styles.miniExt} />
                                    </a>
                                ) : (
                                    <button
                                        key={idx}
                                        type="button"
                                        className={styles.footerLink}
                                        onClick={() => handleNavigate(item.path)}
                                    >
                                        <FaChevronRight className={styles.linkArrow} />
                                        <span>{item.label}</span>
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    {/* Column 4: Delegates & Travel */}
                    <div className="col-6 col-md-4 col-lg-2">
                        <h4 className={styles.columnTitle}>Delegates & Travel</h4>
                        <div className={styles.linkList}>
                            {DELEGATE_LINKS.map((item, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={styles.footerLink}
                                    onClick={() => handleNavigate(item.path)}
                                >
                                    <FaChevronRight className={styles.linkArrow} />
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Column 5: Secretariat & Helpdesk */}
                    <div className="col-12 col-md-12 col-lg-2">
                        <h4 className={styles.columnTitle}>Secretariat & Help</h4>
                        <p className={styles.contactIntro}>
                            For registration, visa clearance, or sponsorship inquiries:
                        </p>

                        <div className={styles.contactCards}>
                            <a
                                href="mailto:ivec2027@gmail.com"
                                className={styles.contactCardLink}
                                title="Send email to conference secretariat"
                            >
                                <div className={styles.contactCardIcon}>
                                    <FaEnvelope />
                                </div>
                                <div className={styles.contactCardContent}>
                                    <span className={styles.contactCardLabel}>Official Email</span>
                                    <strong className={styles.contactCardValue}>
                                        ivec2027@gmail.com
                                    </strong>
                                </div>
                            </a>

                            <div className={styles.contactCardStatic}>
                                <div className={styles.contactCardIcon}>
                                    <FaMapMarkerAlt />
                                </div>
                                <div className={styles.contactCardContent}>
                                    <span className={styles.contactCardLabel}>Conference City</span>
                                    <strong className={styles.contactCardValue}>
                                        Bengaluru, Karnataka, India
                                    </strong>
                                </div>
                            </div>

                            <div className={styles.contactCardStatic}>
                                <div className={styles.contactCardIcon}>
                                    <FaGlobe />
                                </div>
                                <div className={styles.contactCardContent}>
                                    <span className={styles.contactCardLabel}>Official Domain</span>
                                    <strong className={styles.contactCardValue}>
                                        ivec2027.org.in
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* -------------------------------------------------------------
                    3. BOTTOM COPYRIGHT & BACK TO TOP BAR
                ------------------------------------------------------------- */}
                <div className={styles.footerBottom}>
                    <div className={styles.bottomInfo}>
                        <div className={styles.copyrightText}>
                            © 2027 International Vacuum Electronics Conference (IVEC 2027). All
                            rights reserved.
                        </div>
                        <div className={styles.organizedText}>
                            Jointly organized by VEDAS & MTRDC (DRDO), Bengaluru • Technical
                            Co-Sponsorship by IEEE Electron Devices Society (EDS)
                        </div>
                    </div>

                    <div className={styles.bottomActions}>
                        <button
                            type="button"
                            className={styles.backToTopBtn}
                            onClick={scrollToTop}
                            aria-label="Scroll back to top of page"
                        >
                            <span>Back to Top</span>
                            <FaArrowUp className={styles.topIcon} />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
